<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Partners') }}</h1>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="mt-6 shadow sm:rounded-lg bg-white">
                <loading :status="loading"/>
                <div class="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200 sm:px-6">
                    <div>
                        <label class="sr-only" for="search">{{ $t('Search') }}</label>
                        <div class="flex rounded-md shadow-sm">
                            <div class="relative flex-grow focus-within:z-10">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                </div>
                                <input
                                    id="search"
                                    v-model.lazy="search"
                                    :placeholder="$t('Search by name or email')"
                                    class="form-input block border-gray-300 w-full rounded-md pl-10 text-sm transition ease-in-out duration-150"
                                    @change="getPartners"
                                >
                            </div>
                        </div>
                    </div>
                </div>
                <template v-if="partnerList.length > 0">
                    <div class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead>
                            <tr>
                                <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">{{ $t('Name') }}</th>
                                <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">{{ $t('Email') }}</th>
                                <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">{{ $t('Frontend') }}</th>
                                <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">{{ $t('Status') }}</th>
                                <th class="px-6 py-2 text-left text-xs leading-4 font-medium text-gray-600 uppercase tracking-wider">{{ $t('Created at') }}</th>
                            </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-100">
                            <tr v-for="partner in partnerList" class="hover:bg-gray-50">
                                <td class="px-6 py-4 whitespace-no-wrap">
                                    <div class="flex items-center">
                                        <img
                                            :alt="$t('Avatar')"
                                            :src="partner.avatar !== 'gravatar' ? partner.avatar : partner.gravatar"
                                            class="h-8 w-8 rounded-full mr-3"
                                        >
                                        <div class="text-sm font-medium text-gray-900">{{ partner.name }}</div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-no-wrap text-sm text-gray-500">{{ partner.email }}</td>
                                <td class="px-6 py-4 whitespace-no-wrap text-sm text-gray-500">{{ partner.frontend_name || '-' }}</td>
                                <td class="px-6 py-4 whitespace-no-wrap text-sm">
                                    <span v-if="partner.status" class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                        {{ $t('Active') }}
                                    </span>
                                    <span v-else class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                        {{ $t('Inactive') }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-no-wrap text-sm text-gray-500">
                                    {{ partner.created_at | momentFormatDate }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </template>
                <template v-else-if="!loading">
                    <div class="h-full flex">
                        <div class="m-auto">
                            <div class="grid grid-cols-1 justify-items-center h-full w-full py-24">
                                <div class="flex justify-center items-center">
                                    <svg-vue class="h-full h-auto w-48 mb-6" icon="undraw.task-list"></svg-vue>
                                </div>
                                <div class="flex justify-center items-center">
                                    <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <nav class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div class="hidden sm:block">
                        <p class="text-sm leading-5 text-gray-700">
                            {{ $t('Showing') }}
                            <span class="font-medium">{{ (pagination.perPage * pagination.currentPage) - pagination.perPage + 1 }}</span>
                            {{ $t('to') }}
                            <span class="font-medium">{{ pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total }}</span>
                            {{ $t('of') }}
                            <span class="font-medium">{{ pagination.total }}</span>
                            {{ $t('results') }}
                        </p>
                    </div>
                    <div class="flex-1 flex justify-between sm:justify-end">
                        <button
                            :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
                            :disabled="pagination.currentPage <= 1"
                            class="pagination-link"
                            type="button"
                            @click="changePage(pagination.currentPage - 1)"
                        >
                            {{ $t('Previous') }}
                        </button>
                        <button
                            :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
                            :disabled="pagination.currentPage >= pagination.totalPages"
                            class="ml-3 pagination-link"
                            type="button"
                            @click="changePage(pagination.currentPage + 1)"
                        >
                            {{ $t('Next') }}
                        </button>
                    </div>
                </nav>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "employee-partners",
    metaInfo() {
        return {
            title: this.$i18n.t('Partners')
        }
    },
    data() {
        return {
            loading: true,
            search: '',
            page: 1,
            perPage: 10,
            partnerList: [],
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
        }
    },
    filters: {
        momentFormatDate: function (value) {
            return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format);
        },
    },
    mounted() {
        this.getPartners();
    },
    methods: {
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getPartners();
            }
        },
        getPartners() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/employee/partners', {
                params: {
                    page: self.page,
                    perPage: self.perPage,
                    search: self.search,
                }
            }).then(function (response) {
                self.partnerList = response.data.items;
                self.pagination = response.data.pagination;
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
    }
}
</script>
